/**
 * Column mapping utilities for matching saved column names with new Excel columns
 */

import { ExcelColumn } from "../types";

export interface ColumnMatch {
  savedColumnId: string;
  savedColumnName: string;
  matchedColumn: ExcelColumn | null;
  matchType: "exact" | "case-insensitive" | "fuzzy" | "manual" | "missing";
  confidence?: number;
}

/**
 * Calculate similarity between two strings (Levenshtein distance)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const distance = levenshteinDistance(longer.toLowerCase(), shorter.toLowerCase());
  return (longer.length - distance) / longer.length;
}

function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
}

/**
 * Map saved column names to new Excel columns
 */
export function mapColumns(
  savedColumnMappings: Array<{ originalName: string; columnId: string; type: string; category: string }>,
  newExcelColumns: ExcelColumn[]
): ColumnMatch[] {
  const matches: ColumnMatch[] = [];
  
  for (const savedCol of savedColumnMappings) {
    let bestMatch: ExcelColumn | null = null;
    let bestMatchType: ColumnMatch["matchType"] = "missing";
    let bestConfidence = 0;
    
    // Try exact match
    const exactMatch = newExcelColumns.find(
      (col) => col.name === savedCol.originalName
    );
    if (exactMatch) {
      bestMatch = exactMatch;
      bestMatchType = "exact";
      bestConfidence = 1.0;
    } else {
      // Try case-insensitive match
      const caseInsensitiveMatch = newExcelColumns.find(
        (col) => col.name.toLowerCase() === savedCol.originalName.toLowerCase()
      );
      if (caseInsensitiveMatch) {
        bestMatch = caseInsensitiveMatch;
        bestMatchType = "case-insensitive";
        bestConfidence = 0.95;
      } else {
        // Try fuzzy match (similarity > 0.8)
        let bestSimilarity = 0;
        for (const col of newExcelColumns) {
          // Only match if types/categories are compatible
          if (
            (savedCol.type === col.type || 
             (savedCol.type === "date" && col.type === "string") ||
             (savedCol.type === "string" && col.type === "date")) &&
            savedCol.category === col.category
          ) {
            const similarity = calculateSimilarity(savedCol.originalName, col.name);
            if (similarity > bestSimilarity && similarity > 0.8) {
              bestSimilarity = similarity;
              bestMatch = col;
              bestMatchType = "fuzzy";
              bestConfidence = similarity;
            }
          }
        }
      }
    }
    
    matches.push({
      savedColumnId: savedCol.columnId,
      savedColumnName: savedCol.originalName,
      matchedColumn: bestMatch,
      matchType: bestMatchType,
      confidence: bestConfidence,
    });
  }
  
  return matches;
}

/**
 * Check if all required columns are matched
 */
export function validateColumnMapping(matches: ColumnMatch[]): {
  isValid: boolean;
  missingColumns: string[];
  warnings: string[];
} {
  const missingColumns: string[] = [];
  const warnings: string[] = [];
  
  for (const match of matches) {
    if (match.matchType === "missing") {
      missingColumns.push(match.savedColumnName);
    } else if (match.matchType === "fuzzy" && match.confidence && match.confidence < 0.9) {
      warnings.push(
        `"${match.savedColumnName}" matched with "${match.matchedColumn?.name}" (confidence: ${Math.round(match.confidence * 100)}%)`
      );
    }
  }
  
  return {
    isValid: missingColumns.length === 0,
    missingColumns,
    warnings,
  };
}

/**
 * Detect new columns in the uploaded Excel that are not in the saved configuration
 * Uses case-insensitive matching
 */
export function detectNewColumns(
  savedColumnMappings: Array<{ originalName: string; columnId: string; type: string; category: string }>,
  newExcelColumns: ExcelColumn[]
): ExcelColumn[] {
  const savedColumnNames = new Set(
    savedColumnMappings.map(col => col.originalName.toLowerCase().trim())
  );
  
  return newExcelColumns.filter(
    col => !savedColumnNames.has(col.name.toLowerCase().trim())
  );
}

/**
 * Full column comparison result
 */
export interface ColumnComparisonResult {
  matchingColumns: ColumnMatch[];
  missingColumns: string[];
  newColumns: ExcelColumn[];
  isValid: boolean;
  warnings: string[];
}

/**
 * Compare saved config columns with new Excel columns
 * Returns matching, missing, and new columns
 */
export function compareColumns(
  savedColumnMappings: Array<{ originalName: string; columnId: string; type: string; category: string }>,
  newExcelColumns: ExcelColumn[]
): ColumnComparisonResult {
  const matches = mapColumns(savedColumnMappings, newExcelColumns);
  const validation = validateColumnMapping(matches);
  const newColumns = detectNewColumns(savedColumnMappings, newExcelColumns);
  
  return {
    matchingColumns: matches.filter(m => m.matchType !== "missing"),
    missingColumns: validation.missingColumns,
    newColumns,
    isValid: validation.isValid,
    warnings: validation.warnings,
  };
}

